import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, mono, GREEN } from '../bfsi/bfsiShared';
import { qrGet, qrPost, fmtDate } from './qrApi';

const STATES = ['', 'ISSUED', 'ON_DEVICE', 'OFF_DEVICE', 'VOID'];
const PAGE = 50;

export function QrPassportsList({ tenantId, batchId }) {
  const [state, setState] = useState('');
  const [bindMethod, setBindMethod] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  const [voiding, setVoiding] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    const p = new URLSearchParams({ limit: PAGE, offset });
    if (tenantId) p.set('tenant_id', tenantId);
    if (batchId) p.set('batch_id', batchId);
    if (state) p.set('state', state);
    if (q) p.set('q', q);
    if (bindMethod) p.set('bind_method', bindMethod);
    qrGet(`/passports?${p}`).then(setData).catch(e => toast.error(e.message));
  }, [tenantId, batchId, state, q, bindMethod, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [tenantId, batchId, state, q, bindMethod]);

  const doVoid = async () => {
    try {
      await qrPost(`/passports/${voiding.id}/void`, { reason });
      toast.success(`${voiding.short_code} voided`);
      setVoiding(null); setReason(''); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="qr-passports-list" className="p-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b" style={{ borderColor: '#F3F1EA' }}>
        <h2 className="text-base font-semibold mr-auto" style={{ color: '#1B1D1C' }}>Passports <span className="text-sm font-normal" style={{ color: '#9a9e96' }}>({data.total})</span></h2>
        <select data-testid="qr-list-state" value={state} onChange={e => setState(e.target.value)} className="h-8 rounded border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
          {STATES.map(s => <option key={s} value={s}>{s || 'All states'}</option>)}
        </select>
        <select data-testid="qr-list-bind-method" value={bindMethod} onChange={e => setBindMethod(e.target.value)} className="h-8 rounded border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }} title="How the latest binding was made">
          <option value="">Any bind</option><option value="SCAN">Scanned</option><option value="TYPED">Typed (manual)</option>
        </select>
        <Input data-testid="qr-list-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Short code" className="h-8 w-40 bg-white border-[#D9D6CD]" />
      </div>
      <table className="w-full text-sm">
        <thead style={{ background: '#FAF9F6', color: '#6b7069' }}>
          <tr className="text-left"><th className="px-4 py-2 font-medium">Short code</th><th className="px-4 py-2 font-medium">Book · seq</th><th className="px-4 py-2 font-medium">State</th><th className="px-4 py-2 font-medium">Customer</th><th className="px-4 py-2 font-medium">Carton</th><th className="px-4 py-2 font-medium">Bind</th><th className="px-4 py-2 font-medium">Issued</th><th className="px-4 py-2 font-medium"></th></tr>
        </thead>
        <tbody>
          {data.items.map(p => (
            <tr key={p.id} data-testid={`qr-passport-${p.id}`} className="border-t" style={{ borderColor: '#F3F1EA' }}>
              <td className="px-4 py-2" style={mono}>{p.short_code}</td>
              <td className="px-4 py-2 text-xs" style={mono} data-testid={`qr-passport-seq-${p.short_code}`}>{p.seq ? `#${p.seq}` : '—'}</td>
              <td className="px-4 py-2"><span className="px-2 py-0.5 rounded text-xs" style={{ background: p.state === 'VOID' ? '#FDECEC' : '#E7F2EE', color: p.state === 'VOID' ? '#B42318' : GREEN }}>{p.state}</span>
                {p.void_reason && <div className="text-xs" style={{ color: '#9a9e96' }}>{p.void_reason}</div>}</td>
              <td className="px-4 py-2">{p.tenant_name || '—'}</td>
              <td className="px-4 py-2" style={mono}>{p.parent_short_code || (p.parent_id ? '' : '—')}</td>
              <td className="px-4 py-2 text-xs" data-testid={`qr-passport-bind-${p.short_code}`}>{p.bind_method === 'TYPED'
                ? <span className="px-1.5 py-px rounded font-semibold uppercase tracking-wide text-[10px]" style={{ background: '#FBF3DE', color: '#7A5C13' }} title="Code typed in by hand — no camera scan">typed</span>
                : p.bind_method === 'SCAN' ? <span style={{ color: '#6b7069' }}>scanned</span> : <span style={{ color: '#b8bdb9' }}>—</span>}</td>
              <td className="px-4 py-2 text-xs" style={{ color: '#6b7069' }}>{fmtDate(p.created_at)}</td>
              <td className="px-4 py-2 text-right">
                {p.state !== 'VOID' && (
                  voiding?.id === p.id ? (
                    <span className="inline-flex items-center gap-2">
                      <Input data-testid="qr-void-reason" autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason" className="h-7 w-40 bg-white border-[#D9D6CD]" />
                      <Button data-testid="qr-void-confirm" size="sm" className="h-7 text-white" style={{ background: '#B42318' }} disabled={!reason.trim()} onClick={doVoid}>Void</Button>
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => setVoiding(null)}>Cancel</Button>
                    </span>
                  ) : <button data-testid={`qr-void-${p.id}`} className="text-xs hover:underline" style={{ color: '#B42318' }} onClick={() => { setVoiding(p); setReason(''); }}>Void</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-end gap-2 px-4 py-2 text-xs border-t" style={{ borderColor: '#F3F1EA', color: '#6b7069' }}>
        {offset + 1}–{Math.min(offset + PAGE, data.total)} of {data.total}
        <Button data-testid="qr-list-prev" size="sm" variant="outline" className="h-7" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE))}>Prev</Button>
        <Button data-testid="qr-list-next" size="sm" variant="outline" className="h-7" disabled={offset + PAGE >= data.total} onClick={() => setOffset(o => o + PAGE)}>Next</Button>
      </div>
    </Card>
  );
}
